import {type CSSProperties, type MouseEvent as ReactMouseEvent} from 'react'
import './UserAvatar.css'

interface UserAvatarProps {
  /** Stable identifier (user.id / author.id) — drives the gradient palette */
  seed: string | null
  /** Presigned avatar URL — when set, replaces the gradient + initials */
  imageUrl?: string | null
  /** Up to 2 initials shown over the gradient when there's no image */
  initials?: string | null
  /** Pixel size of the round avatar */
  size?: number
  /** Render the violet ring highlight (active page indicator) */
  active?: boolean
  /** Render as a button with a click handler instead of a span */
  onClick?: (e: ReactMouseEvent<HTMLButtonElement>) => void
  /** Accessible label when used as a button */
  ariaLabel?: string
  /** ARIA expanded state when the avatar opens a menu */
  ariaExpanded?: boolean
  /** ARIA haspopup hint */
  ariaHasPopup?: 'menu' | 'true'
  className?: string
}

// Fixed palette of gradient stops; pair index derived from a stable hash of seed
const COLOR_POOL = [
  '#7C5CFF',
  '#A78BFA',
  '#FF7A6B',
  '#FFB088',
  '#86EFAC',
  '#7DD3FC',
  '#F0ABFC',
  '#FACC15',
  '#FB923C',
  '#F472B6',
  '#1E4D3F',
  '#1B5E8C',
]

const SEED_PALETTE: [string, string] = ['#7C5CFF', '#FF7A6B']

const hashString = (s: string): number => {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

const pickGradient = (seed: string | null): [string, string] => {
  if (!seed) return SEED_PALETTE
  const h = hashString(seed)
  const a = COLOR_POOL[h % COLOR_POOL.length]!
  const b = COLOR_POOL[(h * 7 + 3) % COLOR_POOL.length]!
  return [a, b]
}

const UserAvatar = ({
  seed,
  imageUrl,
  initials,
  size = 32,
  active,
  onClick,
  ariaLabel,
  ariaExpanded,
  ariaHasPopup,
  className,
}: UserAvatarProps) => {
  const [av1, av2] = pickGradient(seed)
  const style: CSSProperties = {
    '--av1': av1,
    '--av2': av2,
    '--size': `${size}px`,
    '--font-size': `${Math.max(10, Math.round(size * 0.38))}px`,
  } as CSSProperties

  const cls = ['user-avatar', active ? 'is-active' : '', className].filter(Boolean).join(' ')

  const inner = (
    <>
      {imageUrl ? <img src={imageUrl} alt="" /> : null}
      {!imageUrl && initials ? <span className="user-avatar-initials">{initials}</span> : null}
    </>
  )

  if (onClick) {
    return (
      <button
        type="button"
        className={cls}
        style={style}
        onClick={onClick}
        aria-label={ariaLabel}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup}
      >
        {inner}
      </button>
    )
  }

  return (
    <span className={cls} style={style} aria-hidden={!ariaLabel} aria-label={ariaLabel}>
      {inner}
    </span>
  )
}

export default UserAvatar
